/**
 * The text-contrast lint rule. Async: it rasterizes each frame and samples the real rendered pixels
 * behind every text element, so contrast is correct even over images, gradients, and video where CSS
 * colours alone can't tell you the background. Reports each element once, at its worst frame.
 */
import {
    contrastRatio,
    isLargeText,
    meetsAA,
    parseColor,
    sampleBackgroundUnderGlyph,
    type ImageLike,
} from './contrast';
import { describeElement } from './measure';
import type { ElementMeasurement } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

// Contrast is only assessed where the text is (near-)fully rendered. Below this, a fading glyph is
// rasterized as a blend of foreground and background; those blended pixels sit far enough from the
// full-strength CSS `color` that the sampler would misread them as background and under-report the
// ratio — a false positive on text that merely fades in/out. A translucent `color` alpha causes the
// same blend, so it's gated too.
const OPAQUE_THRESHOLD = 0.95;

/**
 * Whether a measured text element is rendered solidly enough at this frame to assess its contrast:
 * it has measurable text, is near-fully opaque, and its colour is not translucent. Fade frames and
 * translucent colours are skipped rather than risk a blended-pixel false positive.
 */
export function shouldAssessContrast(measurement: ElementMeasurement): boolean {
    if (!measurement.hasText || measurement.fontSizePx <= 0) return false;
    if (measurement.opacity < OPAQUE_THRESHOLD) return false;
    const foreground = parseColor(measurement.color);
    if (!foreground) return false;
    return (foreground.a ?? 1) >= OPAQUE_THRESHOLD;
}

interface Offence {
    element: Element;
    ratio: number;
    large: boolean;
}

export const textContrastBelowAa: RuntimeLintRule = {
    id: 'text-contrast-below-aa',
    severity: 'error',
    guidance:
        'Keep text contrast at WCAG AA against whatever it actually sits on (4.5:1 for body text, ' +
        '3:1 for large text). Over a busy image or gradient, add a scrim/overlay or a solid text ' +
        'background so the copy stays legible.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const frames = context.frames ?? [];
        if (frames.length === 0) return [];

        // Worst (lowest) failing ratio per element across frames.
        const worst = new Map<Element, Offence>();
        for (const frame of frames) {
            const texts = frame.elements.filter(shouldAssessContrast);
            if (texts.length === 0) continue;

            let image: ImageLike | null = null;
            try {
                const canvas = await frame.rasterize();
                const context2d = canvas.getContext('2d');
                if (!context2d) continue;
                image = context2d.getImageData(0, 0, canvas.width, canvas.height);
            } catch {
                continue; // a frame that won't rasterize can't be assessed; skip it
            }

            for (const measurement of texts) {
                const foreground = parseColor(measurement.color);
                if (!foreground) continue;
                const background = sampleBackgroundUnderGlyph(
                    image,
                    measurement.inkRect,
                    foreground,
                );
                if (!background) continue;
                const ratio = contrastRatio(foreground, background);
                const large = isLargeText(measurement.fontSizePx, measurement.bold);
                if (meetsAA(ratio, large)) continue;
                const previous = worst.get(measurement.element);
                if (!previous || ratio < previous.ratio) {
                    worst.set(measurement.element, { element: measurement.element, ratio, large });
                }
            }
        }

        return [...worst.values()].map(
            ({ element, ratio, large }) =>
                `${describeElement(element)} renders at ${ratio.toFixed(2)}:1 contrast against its ` +
                `background — below WCAG AA (${large ? '3:1 for large text' : '4.5:1 for body text'}). ` +
                `Adjust the colour, or add a scrim behind the text.`,
        );
    },
};
